import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { cleanPageTransition, isWalletBrowser, isMobile } from "@/utils/mobile-compatibility";
import { ParticleBackgroundRedGreen } from '@/components/effects/ParticleBackgroundRedGreen';
import { ParticleBackgroundPurpleBlue } from '@/components/effects/ParticleBackgroundPurpleBlue';
import { ParticleBackground } from '@/components/effects/ParticleBackground';
import { useTheme } from "@/providers/ThemeProvider";
import { useAutoWalletSignIn } from "@/hooks/useAutoWalletSignIn";
import { useWalletAuthSync } from "@/hooks/useWalletAuthSync";
import { MatrixOverlay } from '@/components/ui/MatrixOverlay';
import SmokeOverlay from '@/components/ui/SmokeOverlay';
import ParticleFieldOverlay from '@/components/ui/ParticleFieldOverlay';
import WaveOverlay from '@/components/ui/WaveOverlay';
import AuroraOverlay from '@/components/ui/AuroraOverlay';
import FloatingOrbsOverlay from '@/components/ui/FloatingOrbsOverlay';
import BubbleOverlay from '@/components/ui/BubbleOverlay';
import PlasmaOverlay from '@/components/ui/PlasmaOverlay';
import PlasmaSmokeOverlay from '@/components/ui/PlasmaSmokeOverlay';
import LightningOverlay from '@/components/ui/LightningOverlay';
import StarfieldOverlay from '@/components/ui/StarfieldOverlay';
import SnowOverlay from '@/components/ui/SnowOverlay';
import RainOverlay from '@/components/ui/RainOverlay';
import VortexOverlay from '@/components/ui/VortexOverlay';
import FireworksOverlay from '@/components/ui/FireworksOverlay';
import ConstellationOverlay from '@/components/ui/ConstellationOverlay';
import NeuralNetworkOverlay from '@/components/ui/NeuralNetworkOverlay';
import PulseRingsOverlay from '@/components/ui/PulseRingsOverlay';
import GeometricOverlay from '@/components/ui/GeometricOverlay';
import HexagonGridOverlay from '@/components/ui/HexagonGridOverlay';
import CyberGridOverlay from '@/components/ui/CyberGridOverlay';
import DigitalOverlay from '@/components/effects/DigitalOverlay';
import DNAHelixOverlay from '@/components/ui/DNAHelixOverlay';
import FlowFieldOverlay from '@/components/ui/FlowFieldOverlay';

interface BaseLayoutProps {
  children?: React.ReactNode;
  className?: string;
}

export function BaseLayout({ children, className = "min-h-screen flex flex-col bg-gradient animated-gradient-frame" }: BaseLayoutProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const isSmartContractLocker = location.pathname === '/smartcontractlocker';
  const isTokenLogo = location.pathname === '/tokenlogo';
  const isBlogPage = location.pathname.startsWith('/blog');
  const isCreatePage = location.pathname === '/create';
  const isMatrixScanPage = location.pathname === '/mdbmatrixcontractscan';
  
  // Background effect state
  const [backgroundEffect, setBackgroundEffect] = useState(() => {
    return localStorage.getItem('backgroundEffect') || 'plasmasmoke';
  });
  
  // Button layout state
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom'>((localStorage.getItem('buttonLayout') as 'Top' | 'Bottom') || 'Top');

  // Enable auto-sign-in for connected wallets with existing accounts
  useAutoWalletSignIn();
  
  // Sync wallet connection with authentication state
  useWalletAuthSync();

  // Listen for background effect changes
  useEffect(() => {
    console.log('[BaseLayout] Initial background effect:', backgroundEffect);
    
    const handleEffectChange = (event: CustomEvent) => {
      console.log('[BaseLayout] Background effect changed to:', event.detail);
      setBackgroundEffect(event.detail);
    };
    
    window.addEventListener('backgroundEffectChanged', handleEffectChange as EventListener);
    
    return () => {
      window.removeEventListener('backgroundEffectChanged', handleEffectChange as EventListener);
    };
  }, [backgroundEffect]);

  // Listen for button layout changes (Top / Bottom)
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === 'Top' || e.detail === 'Bottom') setButtonLayout(e.detail);
    };
    window.addEventListener('buttonLayoutChanged', handler as EventListener);
    return () => window.removeEventListener('buttonLayoutChanged', handler as EventListener);
  }, []);

  useEffect(() => {
    console.log('Route changed:', location.pathname);
    
    // Apply special handling for mobile wallet browsers
    if (isMobile && isWalletBrowser()) {
      console.log('Applying mobile wallet browser page transition fix');
      cleanPageTransition();
      
      // Force scroll to top on route change
      window.scrollTo(0, 0);
      
      // Add a small delay to ensure the page is fully rendered
      const timer = setTimeout(() => {
        // Force a reflow to ensure the page is properly displayed
        document.body.style.opacity = '0.99';
        setTimeout(() => {
          document.body.style.opacity = '';
        }, 0);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  return (
    <div className="min-h-screen overflow-x-hidden relative" style={{ background: 'transparent' }}>
      {/* Only show page-specific particle effects if background effect is 'off' */}
      {backgroundEffect === 'off' && (
        <>
          {isSmartContractLocker && (
            <>
              {/* Gradient Background for Smart Contract Locker */}
              <div className={`fixed inset-0 opacity-30 ${
                theme === 'light' 
                  ? 'bg-gradient-to-br from-red-500 via-background to-green-500'
                  : 'bg-gradient-to-br from-green-500 via-background to-red-500'
              }`} style={{ zIndex: 0 }} />
              
              {/* Particle Effect for Smart Contract Locker */}
              <ParticleBackgroundRedGreen />
            </>
          )}

          {isTokenLogo && (
            <>
              {/* Gradient Background for Token Logo */}
              <div className={`fixed inset-0 opacity-30 ${
                theme === 'light' 
                  ? 'bg-gradient-to-br from-blue-500 via-background to-purple-500'
                  : 'bg-gradient-to-br from-purple-500 via-background to-blue-500'
              }`} style={{ zIndex: 0 }} />
              
              {/* Particle Effect for Token Logo */}
              <ParticleBackgroundPurpleBlue />
            </>
          )}

          {isBlogPage && (
            <>
              {/* Gradient Background for Blog Pages */}
              <div className={`fixed inset-0 opacity-30 ${
                theme === 'light' 
                  ? 'bg-gradient-to-br from-[#0a74ff] via-background to-[#FFD700]'
                  : 'bg-gradient-to-br from-[#FFD700] via-background to-[#0a74ff]'
              }`} style={{ zIndex: 0 }} />
              
              {/* Particle Effect for Blog Pages */}
              <ParticleBackground />
            </>
          )}

          {isCreatePage && (
            <>
              {/* Gradient Background for Create Page */}
              <div className={`fixed inset-0 opacity-30 ${
                theme === 'light' 
                  ? 'bg-gradient-to-br from-[#0a74ff] via-background to-[#FFD700]'
                  : 'bg-gradient-to-br from-[#FFD700] via-background to-[#0a74ff]'
              }`} style={{ zIndex: 0 }} />
              
              {/* Particle Effect for Create Page */}
              <ParticleBackground />
            </>
          )}
        </>
      )}

      {/* Global Background Effects (exclude Matrix Scan page) */}
      {!isMatrixScanPage && backgroundEffect !== 'off' && (
        <>
          {console.log('[BaseLayout] Rendering effect:', backgroundEffect, 'isMatrixScanPage:', isMatrixScanPage)}
          {backgroundEffect === 'matrix' && <MatrixOverlay forceClassicGreen={isMatrixScanPage} />}
          {backgroundEffect === 'smoke' && <SmokeOverlay />}
          {backgroundEffect === 'particles' && <ParticleFieldOverlay />}
          {backgroundEffect === 'waves' && <WaveOverlay />}
          {backgroundEffect === 'aurora' && <AuroraOverlay />}
          {backgroundEffect === 'orbs' && <FloatingOrbsOverlay />}
          {backgroundEffect === 'bubbles' && <BubbleOverlay />}
          {backgroundEffect === 'plasma' && <PlasmaOverlay />}
          {backgroundEffect === 'plasmasmoke' && <PlasmaSmokeOverlay />}
          {backgroundEffect === 'lightning' && <LightningOverlay />}
          {backgroundEffect === 'starfield' && <StarfieldOverlay />}
          {backgroundEffect === 'snow' && <SnowOverlay />}
          {backgroundEffect === 'rain' && <RainOverlay />}
          {backgroundEffect === 'vortex' && <VortexOverlay />}
          {backgroundEffect === 'fireworks' && <FireworksOverlay />}
          {backgroundEffect === 'constellation' && <ConstellationOverlay />}
          {backgroundEffect === 'neuralnetwork' && <NeuralNetworkOverlay />}
          {backgroundEffect === 'pulserings' && <PulseRingsOverlay />}
          {backgroundEffect === 'geometric' && <GeometricOverlay />}
          {backgroundEffect === 'hexagongrid' && <HexagonGridOverlay />}
          {backgroundEffect === 'cybergrid' && <CyberGridOverlay />}
          {backgroundEffect === 'digital' && <DigitalOverlay />}
          {backgroundEffect === 'dnahelix' && <DNAHelixOverlay />}
          {backgroundEffect === 'flowfield' && <FlowFieldOverlay />}
        </>
      )}
      
      <Header showWalletConnect={true} />
      <main
        className={`pb-8 px-2 sm:px-4 overflow-x-hidden relative z-10 ${
          buttonLayout === 'Bottom'
            ? 'pt-[calc(env(safe-area-inset-top)+var(--wallet-bar-height))]'
            : 'pt-[calc(7.2rem+env(safe-area-inset-top))] md:pt-[calc(7.5rem+env(safe-area-inset-top))] lg:pt-[calc(8rem+env(safe-area-inset-top))]'
        }`}
      >
        {children || <Outlet />}
      </main>
      <Footer className="mt-auto" />
    </div>
  );
}
